import get from 'lodash/get';
import reduce from 'lodash/reduce';
import PropTypes from 'prop-types';
import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';

import {usePageContent} from '@/components/PageContent';
import TableGrid from '@/components/TableGrid';
import {FilterProvider} from '@/contexts/FilterContext';
import {useResponsive} from '@/contexts/ResponsiveContext';
import {TableContextProvider} from '@/contexts/tableGridContact';
import {createContextList} from '@/utils/createContextList';
import {createPaginationHook} from '@/utils/createPaginationHook';

export const
  ADD_HANDLER_DIALOG = 'handler-dialog';

export const createEntityContainer = ({
  module,
  service,
  translation,
  translationPrefix = '',
  relatedKey = 'id',
  breadcrumbName = 'name',
  pageTitle = 'pageTitle',
  route,
  searchPlaceholder,
  pageConfig,
  entityName,
  canSelect = false,
  queryKey,
  showSubtitle,
  ...rest
}) => {

  const useTablePagination = createPaginationHook(queryKey || module, service);

  const {Provider, useList} = createContextList(useTablePagination, module, pageConfig);

  const ContainerList = ({children, top, ...props}) => {
    const {t} = useTranslation(translation || module);
    const {isMobile} = useResponsive();

    const {addCustomBreadcrumbMap} = usePageContent();

    const onMountData = useCallback((data) => {
      addCustomBreadcrumbMap(reduce(data, (acc, item) => {
        let value;
        switch (typeof breadcrumbName) {
          case 'function':
            value = breadcrumbName(item);
            break;
          case 'string':
          default:
            value = get(item, breadcrumbName);
            break;
        }
        return {
          ...acc,
          [`${route}/${item[relatedKey]}`]: value
        };
      }, {}));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <FilterProvider id={module}>
        <TableContextProvider
          {...rest}
          {...props}
          translation={translation}>
          <Provider>
            {top}
            <TableGrid
              pageConfig={pageConfig}
              translation={translation || module}
              scroll={{x: true}}
              addLabel={t(`${translationPrefix}.create`)}
              tableTitle={t(`${translationPrefix}.${pageTitle}`)}
              tableSubTitle={showSubtitle ? t(`${translationPrefix}.subTitle`) : null}
              useTableData={useList}
              canSelect={canSelect}
              showHeader={!isMobile}
              onMountData={onMountData}
              translationPrefix={translationPrefix}
              route={route}
              searchPlaceholder={searchPlaceholder && t(searchPlaceholder)}
              entityName={entityName}
              {...rest}
              {...props}
            />
            {children}
          </Provider>
        </TableContextProvider>
      </FilterProvider>
    );
  };

  ContainerList.propTypes = {
    children: PropTypes.any,
    top: PropTypes.any,
    tabs: PropTypes.any
  };

  return {
    ContainerList,
    useList,
    ListProvider: Provider,
    useTablePagination
  };
};
